import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import LinearProgress from '@material-ui/core/LinearProgress';
import Alert from '@material-ui/lab/Alert';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import Collapse from '@material-ui/core/Collapse';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContentText from '@material-ui/core/DialogContentText';
import { useSnackbar } from 'notistack';
import { exportBackup, importBackup } from './backupService';
import { encryptBackup, decryptBackup, isEncrypted } from './encryptionUtils';
import { getBackupHistory, addBackupToHistory, removeBackupFromHistory, clearBackupHistory, getBackupStatistics } from './backupHistory';
import { shouldShowReminder, dismissReminder, disableReminder, updateLastBackup, getDaysSinceLastBackup } from './backupReminder';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(2),
    paddingTop: theme.spacing(6),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(3),
      paddingTop: theme.spacing(6),
    },
    [theme.breakpoints.up('md')]: {
      paddingLeft: '10%',
      paddingRight: '10%',
      paddingTop: '50px',
    },
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  section: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  buttonGroup: {
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
  },
  progress: {
    marginLeft: theme.spacing(2),
  },
  fileInput: {
    display: 'none',
  },
  warningBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

export default function Backup() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const device = useSelector(state => state.device);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const history = useHistory();

  // Export state
  const [includePIV, setIncludePIV] = useState(true);
  const [includeOpenPGP, setIncludeOpenPGP] = useState(true);
  const [includeOATH, setIncludeOATH] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ percentage: 0, message: '' });

  // Encryption state
  const [enableEncryption, setEnableEncryption] = useState(false);
  const [encryptionPassword, setEncryptionPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Import state
  const [isEncryptedFile, setIsEncryptedFile] = useState(false);
  const [decryptionPassword, setDecryptionPassword] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState(null);
  const [importPIV, setImportPIV] = useState(true);
  const [importOpenPGP, setImportOpenPGP] = useState(true);
  const [pivPin, setPivPin] = useState('123456');
  const [openpgpAdminPin, setOpenpgpAdminPin] = useState('12345678');
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ percentage: 0, message: '' });
  const [lastError, setLastError] = useState(null);
  const [retryOperation, setRetryOperation] = useState(null);

  // Backup history state
  const [backupHistory, setBackupHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyStats, setHistoryStats] = useState(null);

  // Backup reminder state
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [daysSinceBackup, setDaysSinceBackup] = useState(null);

  // Load backup history on mount
  useEffect(() => {
    loadBackupHistory();
    checkBackupReminder();

    // Optimization 2: 检查返回参数
    const params = new URLSearchParams(window.location.search);
    const returnTo = params.get('returnTo');
    const action = params.get('action');

    if (returnTo && action) {
      // 显示提示
      enqueueSnackbar(
        'Backup completed! You can now return to continue the Factory Reset.',
        {
          variant: 'success',
          persist: true,
          action: (key) => (
            <>
              <Button
                color="inherit"
                onClick={() => {
                  closeSnackbar(key);
                  history.push(`/${returnTo}#${action}`);
                }}
              >
                Return to Admin
              </Button>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => closeSnackbar(key)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          )
        }
      );
    }
  }, []);

  const checkBackupReminder = () => {
    if (shouldShowReminder()) {
      const days = getDaysSinceLastBackup();
      setDaysSinceBackup(days);
      // Delay showing the reminder dialog to let the page load first
      setTimeout(() => {
        setShowReminderDialog(true);
      }, 2000);
    }
  };

  const handleReminderDismiss = () => {
    dismissReminder();
    setShowReminderDialog(false);
  };

  const handleReminderDisable = () => {
    disableReminder();
    setShowReminderDialog(false);
    enqueueSnackbar('Backup reminders disabled', { variant: 'info' });
  };

  const handleReminderBackupNow = () => {
    setShowReminderDialog(false);
    // Scroll to export section
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadBackupHistory = () => {
    const history = getBackupHistory();
    const stats = getBackupStatistics();
    setBackupHistory(history);
    setHistoryStats(stats);
  };

  const handleDeleteHistoryItem = (id) => {
    if (removeBackupFromHistory(id)) {
      loadBackupHistory();
      enqueueSnackbar('History item removed', { variant: 'success' });
    } else {
      enqueueSnackbar('Failed to remove history item', { variant: 'error' });
    }
  };

  const handleClearHistory = () => {
    if (clearBackupHistory()) {
      loadBackupHistory();
      enqueueSnackbar('Backup history cleared', { variant: 'success' });
    } else {
      enqueueSnackbar('Failed to clear history', { variant: 'error' });
    }
  };

  // Handle export
  const handleExport = async () => {
    if (!device) {
      enqueueSnackbar('Please connect your device first', { variant: 'error' });
      return;
    }

    // Validate encryption settings
    if (enableEncryption) {
      if (!encryptionPassword || encryptionPassword.length < 6) {
        enqueueSnackbar('Encryption password must be at least 6 characters', { variant: 'error' });
        return;
      }
      if (encryptionPassword !== confirmPassword) {
        enqueueSnackbar('Passwords do not match', { variant: 'error' });
        return;
      }
    }

    setExporting(true);
    setExportProgress({ percentage: 0, message: 'Starting export...' });
    setLastError(null);

    try {
      const backup = await exportBackup(dispatch, {
        includePIV,
        includeOpenPGP,
        includeOATH,
        onProgress: (progress) => {
          setExportProgress(progress);
        }
      });

      let dataToSave;
      let fileExtension = '.json';

      if (enableEncryption) {
        // Encrypt backup
        const json = JSON.stringify(backup, null, 2);
        const encrypted = await encryptBackup(json, encryptionPassword);
        dataToSave = JSON.stringify(encrypted, null, 2);
        fileExtension = '.enc.json';
        enqueueSnackbar('Backup encrypted successfully', { variant: 'success' });
      } else {
        dataToSave = JSON.stringify(backup, null, 2);
      }

      // Create download
      const blob = new Blob([dataToSave], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fileName = `canokey-backup-${new Date().toISOString().split('T')[0]}${fileExtension}`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Save to backup history
      addBackupToHistory({
        fileName: fileName,
        encrypted: enableEncryption,
        device: backup.device,
        includePIV,
        includeOpenPGP,
        includeOATH,
        size: blob.size
      });
      loadBackupHistory();

      // Update backup reminder timestamp
      updateLastBackup();

      enqueueSnackbar('Backup exported successfully', { variant: 'success' });
    } catch (error) {
      const errorInfo = {
        operation: 'export',
        message: error.message,
        timestamp: new Date().toISOString()
      };
      setLastError(errorInfo);
      setRetryOperation(() => handleExport);
      enqueueSnackbar(`Export failed: ${error.message}`, { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportFile(file);
    setImportData(null);
    setIsEncryptedFile(false);
    setDecryptionPassword('');

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);

        // Check if file is encrypted
        if (isEncrypted(data)) {
          setIsEncryptedFile(true);
          enqueueSnackbar('Encrypted backup file detected. Please enter decryption password.', { variant: 'info' });
        } else {
          setImportData(data);
          enqueueSnackbar('Backup file loaded successfully', { variant: 'success' });
        }
      } catch (error) {
        enqueueSnackbar('Invalid backup file format', { variant: 'error' });
        setImportFile(null);
        setImportData(null);
      }
    };
    reader.readAsText(file);
  };

  // Handle decryption
  const handleDecrypt = async () => {
    if (!decryptionPassword) {
      enqueueSnackbar('Please enter decryption password', { variant: 'error' });
      return;
    }

    if (!importFile) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const encryptedData = JSON.parse(e.target.result);
        const decryptedJson = await decryptBackup(encryptedData, decryptionPassword);
        const data = JSON.parse(decryptedJson);
        setImportData(data);
        setIsEncryptedFile(false);
        setLastError(null);
        enqueueSnackbar('Backup decrypted successfully', { variant: 'success' });
      } catch (error) {
        const errorInfo = {
          operation: 'decrypt',
          message: error.message,
          timestamp: new Date().toISOString()
        };
        setLastError(errorInfo);
        setRetryOperation(() => handleDecrypt);
        enqueueSnackbar(`Decryption failed: ${error.message}`, { variant: 'error' });
      }
    };
    reader.readAsText(importFile);
  };

  // Handle retry
  const handleRetry = () => {
    if (retryOperation) {
      setLastError(null);
      retryOperation();
    }
  };

  // Handle dismiss error
  const handleDismissError = () => {
    setLastError(null);
    setRetryOperation(null);
  };

  // Handle import
  const handleImport = async () => {
    if (!device) {
      enqueueSnackbar('Please connect your device first', { variant: 'error' });
      return;
    }

    if (!importData) {
      enqueueSnackbar('Please select a backup file first', { variant: 'error' });
      return;
    }

    setImporting(true);
    setImportProgress({ percentage: 0, message: 'Starting import...' });
    setLastError(null);

    try {
      const results = await importBackup(dispatch, importData, {
        includePIV: importPIV,
        includeOpenPGP: importOpenPGP,
        pivPin,
        openpgpAdminPin,
        onProgress: (progress) => {
          setImportProgress(progress);
        }
      });

      let hasErrors = false;
      if (results.piv.errors.length > 0) {
        hasErrors = true;
        results.piv.errors.forEach(err => {
          enqueueSnackbar(`PIV: ${err}`, { variant: 'error' });
        });
      }
      if (results.openpgp.errors.length > 0) {
        hasErrors = true;
        results.openpgp.errors.forEach(err => {
          enqueueSnackbar(`OpenPGP: ${err}`, { variant: 'error' });
        });
      }

      if (!hasErrors) {
        enqueueSnackbar('Backup restored successfully', { variant: 'success' });
      } else {
        enqueueSnackbar('Backup restored with some errors', { variant: 'warning' });
        const errorInfo = {
          operation: 'import',
          message: 'Partial import with errors',
          details: results,
          timestamp: new Date().toISOString()
        };
        setLastError(errorInfo);
        setRetryOperation(() => handleImport);
      }
    } catch (error) {
      const errorInfo = {
        operation: 'import',
        message: error.message,
        timestamp: new Date().toISOString()
      };
      setLastError(errorInfo);
      setRetryOperation(() => handleImport);
      enqueueSnackbar(`Import failed: ${error.message}`, { variant: 'error' });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className={classes.root}>
      {/* Backup Reminder Dialog */}
      <Dialog
        open={showReminderDialog}
        onClose={handleReminderDismiss}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Backup Reminder</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {daysSinceBackup === null ? (
              <>
                You haven't created a backup yet. Regular backups help protect your device configuration and certificates.
              </>
            ) : (
              <>
                It's been {daysSinceBackup} days since your last backup. Consider creating a new backup to keep your data safe.
              </>
            )}
          </DialogContentText>
          <DialogContentText style={{ marginTop: 16 }}>
            Would you like to create a backup now?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReminderDisable} color="default">
            Don't remind me again
          </Button>
          <Button onClick={handleReminderDismiss} color="default">
            Remind me later
          </Button>
          <Button onClick={handleReminderBackupNow} color="primary" variant="contained">
            Backup Now
          </Button>
        </DialogActions>
      </Dialog>

      <Typography variant="h3" gutterBottom>
        Device Backup & Restore
      </Typography>

      {/* Error Recovery Section */}
      {lastError && (
        <Card className={classes.card} style={{ backgroundColor: '#fff3e0', borderLeft: '4px solid #ff9800' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom style={{ color: '#e65100' }}>
              Operation Failed
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Operation:</strong> {lastError.operation}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Error:</strong> {lastError.message}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Time:</strong> {new Date(lastError.timestamp).toLocaleString()}
            </Typography>
            {lastError.details && (
              <Alert severity="info" style={{ marginTop: 8 }}>
                <Typography variant="body2">
                  Some operations completed successfully. You can retry to attempt the failed operations again.
                </Typography>
              </Alert>
            )}
            <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleRetry}
                disabled={!retryOperation || exporting || importing}
              >
                Retry Operation
              </Button>
              <Button
                variant="outlined"
                onClick={handleDismissError}
              >
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export Section */}
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Export Backup
          </Typography>

          <div className={classes.section}>
            <Typography variant="subtitle1" gutterBottom>
              Select data to backup:
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includePIV}
                  onChange={(e) => setIncludePIV(e.target.checked)}
                  color="primary"
                />
              }
              label="PIV Certificates and Configuration"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeOpenPGP}
                  onChange={(e) => setIncludeOpenPGP(e.target.checked)}
                  color="primary"
                />
              }
              label="OpenPGP Public Keys and Personal Info"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeOATH}
                  onChange={(e) => setIncludeOATH(e.target.checked)}
                  color="primary"
                />
              }
              label="OATH Record Names"
            />
          </div>

          <Alert severity="warning" className={classes.warningBox}>
            <Typography variant="body2">
              <strong>Important:</strong>
            </Typography>
            <Typography variant="body2">
              • Private keys cannot be backed up and will need to be regenerated
            </Typography>
            <Typography variant="body2">
              • OATH secrets cannot be backed up and will need to be re-added
            </Typography>
            <Typography variant="body2">
              • Only certificates, public keys, and configuration data are backed up
            </Typography>
          </Alert>

          <div className={classes.section}>
            <Typography variant="subtitle1" gutterBottom>
              Backup Encryption (Optional):
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={enableEncryption}
                  onChange={(e) => setEnableEncryption(e.target.checked)}
                  color="primary"
                />
              }
              label="Encrypt backup file with password"
            />
            {enableEncryption && (
              <Grid container spacing={2} style={{ marginTop: 8 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Encryption Password"
                    type="password"
                    value={encryptionPassword}
                    onChange={(e) => setEncryptionPassword(e.target.value)}
                    helperText="At least 6 characters"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    helperText="Re-enter password"
                  />
                </Grid>
              </Grid>
            )}
          </div>

          <div className={classes.buttonGroup}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleExport}
              disabled={!device || exporting || (!includePIV && !includeOpenPGP && !includeOATH)}
            >
              Export Backup
            </Button>
            {exporting && <CircularProgress size={24} className={classes.progress} />}
          </div>

          {exporting && exportProgress.message && (
            <div className={classes.section}>
              <Typography variant="body2" gutterBottom>
                {exportProgress.message}
              </Typography>
              <LinearProgress variant="determinate" value={exportProgress.percentage} />
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
                {exportProgress.percentage}% complete
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>

      <Divider />

      {/* Import Section */}
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Import Backup
          </Typography>

          <div className={classes.section}>
            <input
              accept=".json"
              className={classes.fileInput}
              id="backup-file-input"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="backup-file-input">
              <Button variant="outlined" component="span">
                Select Backup File
              </Button>
            </label>
            {importFile && (
              <Typography variant="body2" style={{ marginTop: 8 }}>
                Selected: {importFile.name}
              </Typography>
            )}
          </div>

          {/* Decryption Section for Encrypted Files */}
          {isEncryptedFile && (
            <div className={classes.section}>
              <Alert severity="info" style={{ marginBottom: 16 }}>
                This backup file is encrypted. Please enter the decryption password.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Decryption Password"
                    type="password"
                    value={decryptionPassword}
                    onChange={(e) => setDecryptionPassword(e.target.value)}
                    helperText="Enter the password used to encrypt this backup"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDecrypt}
                    disabled={!decryptionPassword}
                    style={{ height: 56 }}
                  >
                    Decrypt
                  </Button>
                </Grid>
              </Grid>
            </div>
          )}

          {importData && (
            <>
              <div className={classes.section}>
                <Typography variant="subtitle1" gutterBottom>
                  Backup Information:
                </Typography>
                <Typography variant="body2">
                  • Backup Date: {new Date(importData.timestamp).toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  • Device Model: {importData.device?.model || 'Unknown'}
                </Typography>
                <Typography variant="body2">
                  • Firmware Version: {importData.device?.firmwareVersion || 'Unknown'}
                </Typography>
                {importData.piv?.certificates && (
                  <Typography variant="body2">
                    • PIV Certificates: {importData.piv.certificates.length}
                  </Typography>
                )}
                {importData.openpgp?.publicKeys && (
                  <Typography variant="body2">
                    • OpenPGP Keys: {importData.openpgp.publicKeys.length}
                  </Typography>
                )}
                {importData.oath?.records && (
                  <Typography variant="body2">
                    • OATH Records: {importData.oath.records.length}
                  </Typography>
                )}
              </div>

              <div className={classes.section}>
                <Typography variant="subtitle1" gutterBottom>
                  Select data to restore:
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={importPIV}
                      onChange={(e) => setImportPIV(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Restore PIV Certificates"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={importOpenPGP}
                      onChange={(e) => setImportOpenPGP(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Restore OpenPGP Information"
                />
              </div>

              <Grid container spacing={2} className={classes.section}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="PIV PIN"
                    type="password"
                    value={pivPin}
                    onChange={(e) => setPivPin(e.target.value)}
                    helperText="Required for PIV import"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="OpenPGP Admin PIN"
                    type="password"
                    value={openpgpAdminPin}
                    onChange={(e) => setOpenpgpAdminPin(e.target.value)}
                    helperText="Required for OpenPGP import"
                  />
                </Grid>
              </Grid>

              <div className={classes.buttonGroup}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleImport}
                  disabled={!device || importing || (!importPIV && !importOpenPGP)}
                >
                  Restore Backup
                </Button>
                {importing && <CircularProgress size={24} className={classes.progress} />}
              </div>

              {importing && importProgress.message && (
                <div className={classes.section}>
                  <Typography variant="body2" gutterBottom>
                    {importProgress.message}
                  </Typography>
                  <LinearProgress variant="determinate" value={importProgress.percentage} />
                  <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
                    {importProgress.percentage}% complete
                  </Typography>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Divider />

      {/* Backup History Section */}
      <Card className={classes.card}>
        <CardContent>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" gutterBottom>
              Backup History
            </Typography>
            <Button
              onClick={() => setShowHistory(!showHistory)}
              endIcon={showHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {showHistory ? 'Hide' : 'Show'}
            </Button>
          </div>

          <Collapse in={showHistory}>
            {historyStats && (
              <div className={classes.section}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="textSecondary">Total Backups</Typography>
                    <Typography variant="h6">{historyStats.totalBackups}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="textSecondary">Encrypted</Typography>
                    <Typography variant="h6">{historyStats.encryptedBackups}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="textSecondary">Total Size</Typography>
                    <Typography variant="h6">{(historyStats.totalSize / 1024).toFixed(1)} KB</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="textSecondary">Last Backup</Typography>
                    <Typography variant="h6">
                      {historyStats.lastBackupDate ? new Date(historyStats.lastBackupDate).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </div>
            )}

            {backupHistory.length > 0 ? (
              <>
                <TableContainer component={Paper} style={{ marginTop: 16 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>File Name</TableCell>
                        <TableCell>Device Model</TableCell>
                        <TableCell>Data</TableCell>
                        <TableCell>Encrypted</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {backupHistory.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{new Date(item.timestamp).toLocaleString()}</TableCell>
                          <TableCell>{item.fileName}</TableCell>
                          <TableCell>{item.device?.model || 'Unknown'}</TableCell>
                          <TableCell>
                            {[
                              item.includedData.piv && 'PIV',
                              item.includedData.openpgp && 'OpenPGP',
                              item.includedData.oath && 'OATH'
                            ].filter(Boolean).join(', ')}
                          </TableCell>
                          <TableCell>{item.encrypted ? 'Yes' : 'No'}</TableCell>
                          <TableCell>{(item.size / 1024).toFixed(1)} KB</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteHistoryItem(item.id)}
                              color="secondary"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleClearHistory}
                  >
                    Clear All History
                  </Button>
                </div>
              </>
            ) : (
              <Alert severity="info" style={{ marginTop: 16 }}>
                No backup history available. Export your first backup to see it here.
              </Alert>
            )}
          </Collapse>
        </CardContent>
      </Card>
    </div>
  );
}
